import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { Modal, useToast } from '../../components/ui/index.js';
import {
  Form,
  SelectFormField,
  SubmitButton,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { Button } from '../../components/ui/Button.js';
import type { InvitationCreate } from '../../generated/api/types.gen.js';
import { zInvitationCreate } from '../../generated/api/zod.gen.js';
import { createInvitationMutation, listOrgInvitationsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { ASSIGNABLE_ROLES } from './user-labels.js';

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  orgId: number | null;
}

const ROLE_OPTIONS = ASSIGNABLE_ROLES.map((role) => ({
  value: role.value,
  label: role.label,
  description: role.description,
}));

/** Invite a person to the organization by email. Sends the invitation and refreshes the invitation list. */
export const InviteModal: React.FC<InviteModalProps> = ({ open, onClose, orgId }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const form = useZodForm(zInvitationCreate, { defaultValues: { email: '', role: 'viewer', message: '' } });
  const createInvitation = useMutation(createInvitationMutation());

  const close = () => {
    form.reset();
    onClose();
  };

  const send = async (values: InvitationCreate) => {
    if (orgId === null) throw new Error('Select an organization before inviting someone.');
    await createInvitation.mutateAsync({ path: { orgId: String(orgId) }, body: values });
    await queryClient.invalidateQueries({ queryKey: listOrgInvitationsQueryKey({ path: { orgId: String(orgId) } }) });
    notify(`Invitation sent to ${values.email}`);
    close();
  };

  return (
    <Modal open={open} onClose={close} title="Invite user" titleIcon={<Send size={16} className="text-accent-text" />}>
      <Form form={form} onSubmit={send}>
        <TextFormField
          name="email"
          type="email"
          label="Email address"
          placeholder="name@company.com"
          required
          autoComplete="off"
        />
        <SelectFormField name="role" label="Role" options={ROLE_OPTIONS} required />
        <TextAreaFormField
          name="message"
          label="Personal message"
          description="Optional. Shown in the invitation email."
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <SubmitButton>Send invitation</SubmitButton>
        </div>
      </Form>
    </Modal>
  );
};
